/**
 * embeeLAB NFC-Schlüsselanhänger - Konfigurator
 *
 * Stand 07.09.2026, zweite Überarbeitung nach Matthias' Durchsicht:
 *   - Umlaute durchgängig (vorher ae/oe/ue).
 *   - Ziel-URL steht jetzt über der Programmierungsauswahl. Bei "Unbeschrieben"
 *     wird sie geleert und gesperrt - ein leerer Chip hat kein Ziel.
 *   - Die 49 EUR Logo-Einrichtung werden beim Hinzufügen WIRKLICH berechnet:
 *     als zweite Warenkorb-Zeile mit dem Produkt `logo-einrichtung`. Properties
 *     sind in Shopify preisneutral, deshalb geht es nur über ein echtes Produkt.
 *     Ab DATA.logoGebuehr.entfaelltAb Stück wird die Zeile weggelassen.
 *
 * Daten kommen aus dem Data-Island #el-nfc-data (sections/el-product-nfc.liquid).
 * Die Preisstufen werden aus DATA.variants gruppiert - eine Quelle, damit
 * Anzeige und tatsächlicher Shop-Preis nicht auseinanderlaufen können.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-el-nfc-root]');
  if (!root) return;
  const dataEl = document.getElementById('el-nfc-data');
  if (!dataEl) return;
  const DATA = JSON.parse(dataEl.textContent);

  // DATA.variants[].price ist bereits Euro (in Liquid per | divided_by: 100.0 aus
  // dem Shopify-Centwert gerechnet - siehe Kommentar dort zum Anzeigefehler
  // "990 EUR statt 9,90 EUR").
  const STUFEN = (() => {
    const mengen = [...new Set(DATA.variants.map((v) => v.menge))].sort((a, b) => a - b);
    return mengen.map((menge) => {
      const std = DATA.variants.find((v) => v.menge === menge && v.ausfuehrung === 'Standard');
      const prm = DATA.variants.find((v) => v.menge === menge && v.ausfuehrung === 'Premium');
      return {
        menge,
        stkStandard: std ? std.price / menge : null,
        gesamtStandard: std ? std.price : null,
        stkPremium: prm ? prm.price / menge : null,
        gesamtPremium: prm ? prm.price : null,
      };
    }).filter((s) => s.stkStandard !== null);
  })();

  const geld = (euro) => euro.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  // --------------------------------------------------------------- Formen

  const formButtons = root.querySelectorAll('[data-el-form-btn]');
  const formBilder = root.querySelectorAll('[data-form]');       // <img> UND Platzhalter
  const oberflaecheBlock = root.querySelector('[data-el-oberflaeche]');
  const oberflaecheHinweis = root.querySelector('[data-el-oberflaeche-hinweis]');
  const formFeld = root.querySelector('[data-el-form-value]');
  let aktiveForm = null;

  // Foto zur gewählten Form UND Musterung (data-musterung glatt/japandi). Fehlt das
  // Japandi-Foto einer Form, bleibt das glatte stehen - ein ähnliches Foto ist
  // besser als der Platzhalter.
  function bildZeigen() {
    const muster = aktuelleOberflaeche() === 'Japandi-Rillen' ? 'japandi' : 'glatt';
    const art = (el) => el.dataset.musterung || 'glatt';
    const kandidaten = Array.from(formBilder).filter((el) => el.dataset.form === aktiveForm);
    const treffer = kandidaten.find((el) => art(el) === muster)
      || kandidaten.find((el) => art(el) === 'glatt')
      || kandidaten[0];
    formBilder.forEach((el) => el.classList.toggle('is-active', el === treffer));
  }

  function zeigeForm(slug) {
    formButtons.forEach((b) => b.classList.toggle('is-active', b.dataset.elFormBtn === slug));
    aktiveForm = slug;

    const info = DATA.formen.find((f) => f.slug === slug);
    if (formFeld) formFeld.value = info ? info.name : slug;

    // Japandi gibt es nur bei manchen Formen. Die Option wird deaktiviert statt
    // versteckt - sonst verschwindet eine bereits getroffene Wahl unbemerkt.
    const hatJapandi = !!(info && info.japandi);
    if (oberflaecheBlock) {
      const japandi = oberflaecheBlock.querySelector('[value="Japandi-Rillen"]');
      const glatt = oberflaecheBlock.querySelector('[value="Glatt"]');
      if (japandi) {
        japandi.disabled = !hatJapandi;
        if (!hatJapandi && japandi.checked && glatt) glatt.checked = true;
      }
      if (oberflaecheHinweis) oberflaecheHinweis.hidden = hatJapandi;
    }
    // Ein Formwechsel kann die Musterung auf Glatt zurückstellen - dann wird
    // Premium wieder wählbar und das glatte Foto ist das richtige. Beides muss
    // deshalb nach dem Musterungs-Handling laufen.
    bildZeigen();
    premiumSyncen();
    fehlerZuruecksetzen(root.querySelector('[data-el-block="form"]'));
  }

  formButtons.forEach((btn) => btn.addEventListener('click', () => zeigeForm(btn.dataset.elFormBtn)));

  // --------------------------------------------------------------- Menge, Ausführung, Preis

  // Die Vorderseiten-Felder werden hier oben schon geholt, obwohl ihre Logik
  // weiter unten steht: logoGebuehrFaellig() braucht sie, und die Preisberechnung
  // ruft das auf. Stünde die Deklaration erst unten, hinge die Datei an der
  // zufälligen Reihenfolge der Aufrufe (temporal dead zone).
  const vorderseiteRadios = root.querySelectorAll('[name="el-nfc-vorderseite"]');
  const rueckseiteRadios = root.querySelectorAll('[name="el-nfc-rueckseite"]');
  const logoHinweis = root.querySelector('[data-el-logo-hinweis]');
  const logoHinweisHinten = root.querySelector('[data-el-logo-hinweis-hinten]');
  const logosWahl = root.querySelector('[data-el-logos-wahl]');
  const logosRadios = root.querySelectorAll('[name="el-nfc-logos"]');

  const gewaehlt = (radios) => {
    const r = Array.from(radios).find((x) => x.checked);
    return r ? r.value : 'Clean';
  };
  const aktuelleVorderseite = () => gewaehlt(vorderseiteRadios);
  const aktuelleRueckseite = () => gewaehlt(rueckseiteRadios);
  const logoVorn = () => aktuelleVorderseite().startsWith('Eigenes Logo');
  const logoHinten = () => aktuelleRueckseite().startsWith('Eigenes Logo');
  // Die Einrichtung fällt EINMAL an, egal ob das Logo vorne, hinten oder auf
  // beiden Seiten sitzt.
  const eigenesLogoGewaehlt = () => logoVorn() || logoHinten();
  // Gibt es die Variante "Zwei verschiedene Logos" im Shop? Fehlt sie, bleibt
  // es bei der Grundgebühr und der Differenz per Nachtragsrechnung (Rückfall).
  const zweiteVarianteDa = () => !!(DATA.logoGebuehr && DATA.logoGebuehr.variantIdZweiLogos);
  const zweiLogos = () => logoVorn() && logoHinten() && zweiteVarianteDa()
    && gewaehlt(logosRadios) === 'Zwei verschiedene Logos';
  const logoBetrag = () => (zweiLogos() ? DATA.logoGebuehr.preisZweiLogos : DATA.logoGebuehr.preis);
  const logoVariantId = () => (zweiLogos() ? DATA.logoGebuehr.variantIdZweiLogos : DATA.logoGebuehr.variantId);

  const mengeSelect = root.querySelector('[data-el-menge-select]');
  const ausfuehrungRadios = root.querySelectorAll('[name="el-nfc-ausfuehrung"]');
  const preisGesamtEl = root.querySelector('[data-el-preis-gesamt]');
  const preisStueckEl = root.querySelector('[data-el-preis-stueck]');
  const badgeEl = root.querySelector('[data-el-badge]');
  const nudgeEl = root.querySelector('[data-el-nudge]');
  const premiumHinweisEl = root.querySelector('[data-el-premium-hinweis]');
  const summeHinweisEl = root.querySelector('[data-el-summe-hinweis]');

  const aktuelleMenge = () => (mengeSelect ? parseInt(mengeSelect.value, 10) : STUFEN[0].menge) || STUFEN[0].menge;
  const aktuelleAusfuehrung = () => {
    const r = Array.from(ausfuehrungRadios).find((x) => x.checked);
    return r ? r.value : 'Standard';
  };
  const preisAus = (stufe, ausf) => (ausf === 'Premium'
    ? { stueck: stufe.stkPremium, gesamt: stufe.gesamtPremium }
    : { stueck: stufe.stkStandard, gesamt: stufe.gesamtStandard });

  // ------------------------------------------------- Premium schliesst Japandi aus
  // Die UV-Harz-Beschichtung der Premium-Ausführung läuft in die Rillen und ebnet
  // sie ein. Die Japandi-Struktur ist nur 0,4 mm tief - danach wäre von dem Muster
  // nichts mehr übrig, wofür der Kunde bezahlt hat. Die Kombination gibt es deshalb
  // nicht, und zwar gesperrt statt nur im Text erwähnt.
  const oberflaecheRadios = root.querySelectorAll('[name="el-nfc-oberflaeche"]');
  const japandiPremiumHinweis = root.querySelector('[data-el-japandi-premium-hinweis]');

  const aktuelleOberflaeche = () => {
    const r = Array.from(oberflaecheRadios).find((x) => x.checked);
    return r ? r.value : 'Glatt';
  };
  const premiumGesperrt = () => aktuelleOberflaeche() === 'Japandi-Rillen';

  function premiumSyncen() {
    const sperren = premiumGesperrt();
    const standard = Array.from(ausfuehrungRadios).find((x) => x.value === 'Standard');
    ausfuehrungRadios.forEach((r) => {
      if (r.value !== 'Premium') return;
      r.disabled = sperren;
      // Zurückstellen, bevor der Preis neu gerechnet wird - sonst stünde kurz
      // ein Premium-Preis da, den es für diese Musterung gar nicht gibt.
      if (sperren && r.checked && standard) standard.checked = true;
    });
    if (japandiPremiumHinweis) japandiPremiumHinweis.hidden = !sperren;
    preisAktualisieren();
  }

  oberflaecheRadios.forEach((r) => r.addEventListener('change', premiumSyncen));
  oberflaecheRadios.forEach((r) => r.addEventListener('change', bildZeigen));

  function logoGebuehrFaellig() {
    if (!eigenesLogoGewaehlt()) return false;
    if (!DATA.logoGebuehr) return false;
    return aktuelleMenge() < DATA.logoGebuehr.entfaelltAb;
  }

  function preisAktualisieren() {
    const menge = aktuelleMenge();
    const ausf = aktuelleAusfuehrung();
    const stufe = STUFEN.find((s) => s.menge === menge);
    if (!stufe) return;

    const { stueck, gesamt } = preisAus(stufe, ausf);
    const basis = STUFEN[0].stkStandard;
    const ersparnis = Math.round((1 - stueck / basis) * 100);

    if (preisGesamtEl) preisGesamtEl.textContent = geld(gesamt);
    if (preisStueckEl) preisStueckEl.textContent = `${geld(stueck)} je Stück`;
    if (badgeEl) {
      badgeEl.textContent = `${ersparnis} % gespart`;
      badgeEl.hidden = ersparnis <= 0;
    }

    // Was Premium bei DIESER Menge konkret kostet. Aus den echten Variantenpreisen
    // gerechnet, nicht mit 1,40 EUR fest verdrahtet - sonst stimmt der Hinweis
    // nicht mehr, sobald sich die Staffel ändert.
    if (premiumHinweisEl) {
      const aufpreisGesamt = stufe.gesamtPremium - stufe.gesamtStandard;
      const aufpreisStueck = stufe.stkPremium - stufe.stkStandard;
      if (premiumGesperrt()) {
        // Ein Aufpreis für etwas Unbuchbares wäre hier nur verwirrend.
        premiumHinweisEl.textContent = 'Bei Japandi-Rillen nicht wählbar.';
      } else {
        premiumHinweisEl.textContent = ausf === 'Premium'
          ? `Enthaltener Premium-Aufpreis: ${geld(aufpreisGesamt)} (${geld(aufpreisStueck)} je Stück)`
          : `Premium kostet ${geld(aufpreisGesamt)} mehr (${geld(aufpreisStueck)} je Stück)`;
      }
    }

    // Zeigt konkret, wie viel günstiger die nächsthöhere Menge wird.
    if (nudgeEl) {
      const idx = STUFEN.findIndex((s) => s.menge === menge);
      const naechste = STUFEN[idx + 1];
      if (naechste) {
        const np = preisAus(naechste, ausf).stueck;
        const ne = Math.round((1 - np / basis) * 100);
        nudgeEl.textContent = `Ab ${naechste.menge} Stück nur noch ${geld(np)} je Stück - ${ne} % gespart`;
        nudgeEl.hidden = false;
      } else {
        nudgeEl.hidden = true;
      }
    }

    // Wenn die Logo-Einrichtung dazukommt, gehört das VOR dem Klick sichtbar
    // gemacht - eine Überraschung im Warenkorb wäre unseriös.
    if (summeHinweisEl) {
      if (logoGebuehrFaellig()) {
        const g = logoBetrag();
        summeHinweisEl.textContent = `Zzgl. ${geld(g)} einmalige Logo-Einrichtung - Gesamt ${geld(gesamt + g)}`;
        summeHinweisEl.hidden = false;
      } else {
        summeHinweisEl.hidden = true;
      }
    }
  }

  // Die Menge entscheidet mit, ob die Logo-Einrichtung anfällt (sie entfällt ab
  // DATA.logoGebuehr.entfaelltAb Stück). Deshalb hier logoAktualisieren() statt
  // nur preisAktualisieren() - sonst bleibt der Hinweistext beim Mengenwechsel
  // auf dem alten Stand stehen, während die Summe darunter schon stimmt.
  // logoAktualisieren() ruft preisAktualisieren() selbst mit auf.
  if (mengeSelect) mengeSelect.addEventListener('change', logoAktualisieren);
  ausfuehrungRadios.forEach((r) => r.addEventListener('change', preisAktualisieren));

  // --------------------------------------------------------------- Rückseite -> Symbolfarbe

  const symbolfarbe = root.querySelector('[data-el-symbolfarbe]');
  const symbolfarbeBlock = root.querySelector('[data-el-symbolfarbe-block]');
  const symbolfarbeHinweis = root.querySelector('[data-el-symbolfarbe-hinweis]');

  function symbolfarbeAktualisieren() {
    // Das Scan-Symbol kann auf beiden Seiten sitzen. Eine Farbe dafür wird
    // gebraucht, sobald EINE der beiden Seiten eines trägt.
    // Vergleich auf den genauen Wert: "Eigenes Logo" braucht keine Symbolfarbe,
    // ein "alles ausser Clean" würde sie fälschlich zur Pflicht machen.
    const symbolHinten = aktuelleRueckseite() === 'Mit Scan-Symbol';
    const symbolVorn = aktuelleVorderseite() === 'Mit Scan-Symbol';
    const clean = !symbolVorn && !symbolHinten;
    if (symbolfarbe) symbolfarbe.disabled = clean;
    if (symbolfarbeBlock) symbolfarbeBlock.classList.toggle('is-inaktiv', clean);
    if (symbolfarbeHinweis) symbolfarbeHinweis.hidden = !clean;
  }

  // --------------------------------------------------------------- Programmierung -> Ziel-URL
  //
  // Bei "Unbeschrieben" muss die Ziel-URL leer sein: der Kunde bekommt einen
  // leeren Chip, eine Zieladresse in der Bestellung wäre ein Widerspruch und
  // würde bei der Fertigung zu Rückfragen führen.

  const progRadios = root.querySelectorAll('[name="el-nfc-programmierung"]');
  const zielUrl = root.querySelector('[data-el-ziel-url]');
  const zielBlock = root.querySelector('[data-el-block="ziel"]');
  const zielHinweis = root.querySelector('[data-el-ziel-hinweis]');

  function zielUrlAktualisieren() {
    const r = Array.from(progRadios).find((x) => x.checked);
    const unbeschrieben = r && r.value === 'Unbeschrieben';
    if (zielUrl) {
      zielUrl.disabled = !!unbeschrieben;
      if (unbeschrieben) zielUrl.value = '';
    }
    if (zielBlock) zielBlock.classList.toggle('is-inaktiv', !!unbeschrieben);
    if (zielHinweis) {
      zielHinweis.textContent = unbeschrieben
        ? 'Nicht nötig - du erhältst einen leeren Chip und programmierst ihn selbst.'
        : 'Lass das Feld leer, wenn du dich beraten lassen möchtest. Die Beratung nach dem Kauf ist kostenlos.';
    }
  }
  progRadios.forEach((r) => r.addEventListener('change', zielUrlAktualisieren));

  // ------------------------------------- Eigenes Logo (Vorder- und Rückseite)

  function logoText() {
    if (!DATA.logoGebuehr) {
      return 'Schick uns dein Logo nach dem Kauf per Mail an kontakt@embeelab.de. Die einmalige Einrichtung stellen wir separat in Rechnung.';
    }
    if (aktuelleMenge() >= DATA.logoGebuehr.entfaelltAb) {
      return `Ab ${DATA.logoGebuehr.entfaelltAb} Stück entfällt die Einrichtungsgebühr - für dich kostenlos. Logo bitte per Mail an kontakt@embeelab.de.`;
    }
    return `Die einmalige Logo-Einrichtung über ${geld(logoBetrag())} wird mit in den Warenkorb gelegt. Logo bitte per Mail an kontakt@embeelab.de, am besten als SVG.`;
  }

  function beideSeitenText() {
    const wohin = 'Schreib in der Mail kurz, welches Logo auf welche Seite soll.';
    if (!DATA.logoGebuehr) return `Logo auf beiden Seiten. ${wohin}`;
    if (aktuelleMenge() >= DATA.logoGebuehr.entfaelltAb) {
      return `Ab ${DATA.logoGebuehr.entfaelltAb} Stück entfällt die Einrichtung - auch bei zwei verschiedenen Logos. Logos bitte per Mail an kontakt@embeelab.de. ${wohin}`;
    }
    if (!zweiteVarianteDa()) {
      const grund = DATA.logoGebuehr.preis;
      const zwei = DATA.logoGebuehr.preisZweiLogos || 69;
      return `Dasselbe Logo auf beiden Seiten: es bleibt bei ${geld(grund)}. `
        + `Zwei verschiedene Logos: die Einrichtung kostet ${geld(zwei)}, `
        + `die Differenz von ${geld(zwei - grund)} stellen wir separat in Rechnung. ${wohin}`;
    }
    return zweiLogos()
      ? `Die Einrichtung für zwei verschiedene Logos über ${geld(DATA.logoGebuehr.preisZweiLogos)} wird mit in den Warenkorb gelegt. Bitte beide Logos per Mail an kontakt@embeelab.de. ${wohin}`
      : `Dasselbe Logo auf beiden Seiten: es bleibt bei ${geld(DATA.logoGebuehr.preis)}, die Einrichtung wird mit in den Warenkorb gelegt. Logo bitte per Mail an kontakt@embeelab.de, am besten als SVG.`;
  }

  function logoAktualisieren() {
    const vorn = logoVorn();
    const hinten = logoHinten();
    const beide = vorn && hinten;
    // "Welche Logos?" nur bei Logo auf beiden Seiten. Ausgeblendet ist die
    // Auswahl auch gesperrt, damit keine Property "Logos" in der Bestellung landet.
    const wahlAn = beide && zweiteVarianteDa();
    if (logosWahl) logosWahl.hidden = !wahlAn;
    logosRadios.forEach((r) => { r.disabled = !wahlAn; });
    if (logoHinweis) {
      // Bei Logo auf beiden Seiten steht EIN gemeinsamer Hinweis unter der
      // Rückseite - zwei Hinweise mit verschiedenen Beträgen wären verwirrend.
      logoHinweis.hidden = !(vorn && !hinten);
      if (vorn && !hinten) logoHinweis.textContent = logoText();
    }
    if (logoHinweisHinten) {
      logoHinweisHinten.hidden = !hinten;
      if (hinten) logoHinweisHinten.textContent = beide ? beideSeitenText() : logoText();
    }
    preisAktualisieren();
  }
  // Beide Seiten steuern zwei Dinge: ob die Logo-Gebühr anfällt und ob eine
  // Farbe für das Scan-Symbol gebraucht wird.
  logosRadios.forEach((r) => r.addEventListener('change', logoAktualisieren));
  [vorderseiteRadios, rueckseiteRadios].forEach((radios) => radios.forEach((r) => {
    r.addEventListener('change', logoAktualisieren);
    r.addEventListener('change', symbolfarbeAktualisieren);
  }));

  // --------------------------------------------------------------- Validierung
  //
  // EINE Schleife über alle [data-el-required] - kein Block je Produkt wie in
  // el-product-home-living.liquid. Deaktivierte Felder werden übersprungen: sie
  // sind bewusst nicht anwendbar, keine vergessenen Pflichtfelder.

  function fehlerZuruecksetzen(block) {
    if (block) block.classList.remove('has-error');
  }

  function pruefePflichtfelder() {
    for (const feld of root.querySelectorAll('[data-el-required]')) {
      if (feld.disabled) continue;
      // Radiogruppen: es zählt, ob IRGENDEINER der Gruppe gewählt ist.
      if (feld.type === 'radio') {
        const gruppe = root.querySelectorAll(`[name="${feld.name}"]`);
        if (Array.from(gruppe).some((r) => r.checked)) continue;
      } else if ((feld.value || '').trim()) {
        continue;
      }
      const block = feld.closest('[data-el-block]') || feld.parentElement;
      if (block) {
        block.classList.add('has-error');
        block.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return feld.dataset.elError || 'Bitte triff diese Auswahl.';
    }
    return null;
  }

  root.addEventListener('change', (e) => {
    const block = e.target.closest && e.target.closest('[data-el-block]');
    if (block) fehlerZuruecksetzen(block);
  });

  // --------------------------------------------------------------- In den Warenkorb

  const addBtn = root.querySelector('[data-el-add]');
  const errorEl = root.querySelector('[data-el-error-msg]');

  async function inDenWarenkorb() {
    if (!addBtn) return;
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    const fehler = pruefePflichtfelder();
    if (fehler) {
      if (errorEl) { errorEl.hidden = false; errorEl.textContent = fehler; }
      return;
    }

    const menge = aktuelleMenge();
    const ausf = aktuelleAusfuehrung();

    // Sicherheitsnetz: die Musterung kann sich zuletzt geändert haben, ohne dass
    // premiumSyncen() durchgelaufen ist. Lieber hier abbrechen als eine Bestellung
    // annehmen, die so nicht produziert werden kann.
    if (ausf === 'Premium' && premiumGesperrt()) {
      premiumSyncen();
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = 'Premium ist bei Japandi-Rillen nicht möglich. '
          + 'Die Ausführung wurde auf Standard gestellt - bitte prüfe den Preis und leg erneut in den Warenkorb.';
      }
      return;
    }

    const variant = DATA.variants.find((v) => v.menge === menge && v.ausfuehrung === ausf);
    if (!variant) {
      if (errorEl) { errorEl.hidden = false; errorEl.textContent = 'Diese Kombination ist gerade nicht verfügbar.'; }
      return;
    }

    const properties = {};
    root.querySelectorAll('[data-el-property]').forEach((feld) => {
      if (feld.disabled) return;
      if (feld.type === 'radio' && !feld.checked) return;
      const wert = (feld.value || '').trim();
      if (wert) properties[feld.dataset.elProperty] = wert;
    });
    // Sperrt die Warenkorbzeile gegen Mengenänderung (main-cart-items.liquid /
    // cart-drawer.liquid) und verhindert, dass Shopify zwei gleich konfigurierte
    // Zeilen zu quantity:2 zusammenfasst - was die Stückzahl still verdoppeln würde.
    properties._cfg = `nfc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const items = [{ id: variant.id, quantity: 1, properties }];

    // Die Logo-Einrichtung ist eine eigene Warenkorb-Zeile - über eine Property
    // liesse sich in Shopify kein Geld berechnen. Ein Logo = 49-EUR-Variante,
    // zwei verschiedene Logos = 69-EUR-Variante.
    if (logoGebuehrFaellig()) {
      items.push({
        id: logoVariantId(),
        quantity: 1,
        properties: { 'Gehört zu': 'NFC-Schlüsselanhänger', _cfg: properties._cfg },
      });
    }

    addBtn.disabled = true;
    const beschriftung = addBtn.textContent;
    addBtn.textContent = 'Wird hinzugefügt …';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const f = await res.json().catch(() => ({}));
        throw new Error(f.description || f.message || 'Konnte nicht in den Warenkorb gelegt werden.');
      }
      window.location.href = '/cart';
    } catch (err) {
      if (errorEl) { errorEl.hidden = false; errorEl.textContent = err.message; }
      addBtn.disabled = false;
      addBtn.textContent = beschriftung;
    }
  }

  if (addBtn) addBtn.addEventListener('click', inDenWarenkorb);

  // --------------------------------------------------------------- Start

  if (DATA.formen && DATA.formen[0]) zeigeForm(DATA.formen[0].slug);
  symbolfarbeAktualisieren();
  zielUrlAktualisieren();
  logoAktualisieren();      // ruft preisAktualisieren() mit auf
})();
